import React, { type FormEvent } from "react";
import { twMerge } from "tailwind-merge";

import { SupText, SupAssetPair, SupAssetLabel, SupSort, SupChevronRight } from "@repo/core-ui";

interface AmountInputProps {
  value: string;
  prefix?: string;
  hint: string;
  hintPlaceholder: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  balance: string;
  token: {
    icon: string;
    name: string;
    chainIcon: string;
    chainName: string;
  };
  onChange: (value: string, e: FormEvent<HTMLInputElement>) => void;
  onSwitch: () => void;
  onSwitchToken?: () => void;
  disableTokenSwitch?: boolean;
  disableSwitch?: boolean;
}

const NUMBER_REG_EXP = /^-?\d*\.?\d*$/;

export function AmountInput({
  placeholder,
  prefix = "",
  value,
  className,
  inputClassName,
  balance,
  token,
  hint,
  hintPlaceholder,
  onChange,
  onSwitch,
  onSwitchToken,
  disableTokenSwitch,
  disableSwitch,
}: AmountInputProps) {
  const handleStateChange = (e: FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.replace(prefix, "");

    if (e.currentTarget.value === "" || NUMBER_REG_EXP.test(value)) {
      onChange(value, e);
    }
  };

  return (
    <div className={twMerge("sup-amount-input relative w-full border-0.5 border-neutral-300", className)}>
      <input
        type="text"
        className={twMerge(
          "sup-amount-input h-[4.5rem] w-full bg-neutral-25 font-mono font-normal text-2xl leading-none outline-0 pl-2 pt-2 pb-9 pr-36 placeholder:text-mono placeholder:text-2xl placeholder-neutral-300",
          inputClassName
        )}
        placeholder={placeholder}
        value={value ? `${prefix}${value}` : value}
        onChange={handleStateChange}
      />

      {!disableSwitch && (
        <button className="absolute left-2 bottom-4 flex items-center transition-colors duration-200 text-primary-600 hover:text-primary-700 outline-0" onClick={onSwitch}>
          <SupSort height={16} width={16} />

          {hint ? (
            <SupText as="span" variant="subtitle2" className="text-neutral-900 select-none">
              {hint}
            </SupText>
          ) : (
            <SupText as="span" variant="subtitle2" className="text-neutral-600 select-none">
              {hintPlaceholder}
            </SupText>
          )}
        </button>
      )}

      <button
        className={twMerge(
          disableTokenSwitch ? "cursor-default" : "hover:bg-neutral-75",
          "absolute outline-0 transition-colors duration-200 bg-neutral-50 flex items-center gap-3 border-l-0.5 border-b-0.5 rounded-bl-2xl border-neutral-300 p-2 top-0 right-0"
        )}
        onClick={disableTokenSwitch ? undefined : onSwitchToken}
      >
        <SupAssetLabel
          className="p-0.5"
          small
          label={
            <SupText variant="caption" weight="bold">
              {token.name}
            </SupText>
          }
          subLabel={
            <SupText variant="caption" className="text-[10px] leading-none">
              {token.chainName}
            </SupText>
          }
        >
          <SupAssetPair small logo={token.icon} pairLogo={token.chainIcon} />
        </SupAssetLabel>

        {!disableTokenSwitch && <SupChevronRight className="bg-primary-100 rounded-full text-neutral-700" />}
      </button>

      <div className="absolute bottom-2 right-2 flex gap-2 select-none">
        <SupText as="span" variant="subtitle2" className="text-neutral-700">
          Balance
        </SupText>

        <SupText as="span" variant="subtitle2">
          {balance}
        </SupText>
      </div>
    </div>
  );
}
